import { useQueryClient } from '@tanstack/react-query';
import { StatusBar, Modal, PrimaryButton, SecondaryButton, SnackbarStatus } from '@weareopenr/component-library';
import { useForm } from 'react-hook-form';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './EditOptionGroupPricingModal.module.scss';
import { updateOptionGroup } from 'src/api/updateOptionGroup/updateOptionGroup';

import { formatUpdateOptionGroupRequestBody, getOptionGroupDefaultValues } from 'src/helpers/optionGroupHelpers';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { IOptionGroup } from 'src/types/menu.types';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { IOptionGroupValues } from '../CreateOptionGroupModal/types';
import { OptionGroupPricing } from '../OptionGroupPricing';
import { useMemo } from 'react';

interface EditOptionGroupPricingModalContentProps {
  close: () => void;
  itemId?: string;
  optionGroup: IOptionGroup;
}

export interface EditOptionGroupPricingModalProps extends EditOptionGroupPricingModalContentProps {
  isOpen: boolean;
}

const EditOptionGroupPricingModalContent = ({
  close,
  itemId,
  optionGroup,
}: EditOptionGroupPricingModalContentProps) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  // Default values can only be set on initial render, so we can safely ignore the dependencies here
  const defaultValues = useMemo(() => getOptionGroupDefaultValues(optionGroup), []);
  const useFormReturn = useForm<IOptionGroupValues>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useFormReturn;

  const onSubmit = async (data: IOptionGroupValues) => {
    clearError();
    try {
      const updateOptionGroupRequestBody = formatUpdateOptionGroupRequestBody(data, itemId);

      await updateOptionGroup(optionGroup.optionGroupId, updateOptionGroupRequestBody, selectedPriceSchemeId);
      await queryClient.invalidateQueries({
        queryKey: itemId ? [QueryKeys.item, itemId] : [QueryKeys.optionGroups],
      });
      close();

      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Your changes have been successfully saved' });
    } catch (error) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Something went wrong whilst updating the group',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <OptionGroupPricing formReturn={useFormReturn} />
      <ButtonContainer
        left={
          <SecondaryButton
            size="full-width"
            buttonStyle="outline"
            onClick={close}
            type="button"
            disabled={isSubmitting}
          >
            Back
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            size="full-width"
            type="submit"
            key="save"
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
          >
            Save
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export const EditOptionGroupPricingModal = ({
  close,
  optionGroup,
  itemId,
  isOpen,
}: EditOptionGroupPricingModalProps) => {
  return (
    <Modal open={isOpen} closeModal={close}>
      <EditOptionGroupPricingModalContent close={close} optionGroup={optionGroup} itemId={itemId} />
    </Modal>
  );
};

export default EditOptionGroupPricingModal;
